import { useState } from 'react';
import { useBuyMyBestie } from '@hooks/credits/useBuyMyBestie';
import { StatusPayment } from '@components/features/payment';
import { useTranslation } from 'next-i18next';
import { useExtendedRouter } from '@hooks/next-routing-wrappers';
import { PaymentMethodsBestie } from './partials/PaymentMethodsBestie';

interface BuyMyBestieHubProps {
  successCallback?: () => void;
}

const transitionProps = {
  show: true,
  appear: true,
  unmount: false,
  enter: 'transition-opacity duration-300',
  enterFrom: 'opacity-0',
  enterTo: 'opacity-100',
};

/**
 * @description Hub component for the My Bestie purchase modal.
 * Steps: PaymentMethods → Activated/CompletedInvoice/NotCompleted/Error
 */
export const BuyMyBestieHub = (props: BuyMyBestieHubProps) => {
  const { successCallback } = props;

  const [currentStep, setCurrentStep] = useState<string>('PaymentMethods');
  const { t } = useTranslation('common');
  const { router } = useExtendedRouter();

  const {
    cartDetails,
    orderCredits,
    savingOrder,
    paths,
    loadingCartDetails,
  } = useBuyMyBestie({
    stepSetter: setCurrentStep,
    successCallbackFunction: successCallback,
  });

  return (
    <>
      {/* Step 1 — Payment methods */}
      {currentStep === 'PaymentMethods' && !loadingCartDetails && (
        <div className="sm:!min-w-102">
          <div className="px-6 pt-3">
            <p>{t('mc.personal.agent.buy.description')}</p>
          </div>
          <PaymentMethodsBestie
            transitionProps={transitionProps}
            cartDetails={cartDetails}
            orderCredits={orderCredits}
            savingOrder={savingOrder}
          />
        </div>
      )}

      {/* Step 2a — Paid with card, service activated */}
      {currentStep === 'Activated' && (
        <StatusPayment
          orderStatus="success"
          customTitle="mc.personal.agent.activated.title"
          customMessage="mc.personal.agent.activated.message"
          actionSuccess={() => router.push('/my-bestie')}
        />
      )}

      {/* Step 2b — Completed but not paid (invoice/proforma) */}
      {currentStep === 'CompletedInvoice' && (
        <StatusPayment
          orderStatus="success"
          customTitle="order.success.title_candidates"
          customMessage="order.checkout.payment.invoice_candidates"
          customButtonLabel="page.after.activation.job.button.invoices"
          withAction={paths?.toInvoice !== undefined}
          actionSuccess={paths?.toInvoice}
        />
      )}

      {/* Step 2c — Not completed */}
      {currentStep === 'NotCompleted' && (
        <StatusPayment
          orderStatus="error"
          customTitle="order.failed.title_candidates"
          customMessage="order.failed.message_candidate"
          customButtonLabel="global.label.see_details"
          withAction={paths?.notCompleted !== undefined}
          actionError={paths?.notCompleted}
        />
      )}

      {/* Step 2d — Error */}
      {currentStep === 'Error' && (
        <StatusPayment
          orderStatus="error"
          customTitle="payment.error.title_candidates"
          customMessage="payment.error.message_candidate"
          actionError={() => setCurrentStep('PaymentMethods')}
        />
      )}
    </>
  );
};
